import { useCallback, useMemo } from 'react';
import { useRouter } from '@tanstack/react-router';
import { CalendarOff } from 'lucide-react';
import { AdvancedFilterPanel, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { useLastViewedRowStore } from '@/store/lastViewedRow.store';
import { leaveApplicationListInfiniteQueryOptions } from '../api/leaveApplications.api';
import { useLeaveApplicationFilterFields } from '../hooks/useLeaveApplicationFilterFields';
import { useLeaveApplicationFilters } from '../hooks/useLeaveApplicationFilters';
import { isAttendanceClaimSelected } from '../utils/leaveApplicationFilters';
import { getLeaveApplicationColumns } from './LeaveApplicationColumns';
import { LeaveApplicationListHeader } from './LeaveApplicationListHeader';
import { LeaveApplicationSummaryCards } from './LeaveApplicationSummaryCards';
import type { LeaveApplicationListItem } from '../types/leaveApplication.types';

const VIEWED_ROW_LIST = 'leaveApplications';
const NEW_TAB_TARGET = '_blank';
const NEW_TAB_FEATURES = 'noopener,noreferrer';

export function LeaveApplicationListTable() {
  const {
    searchText,
    filterValues,
    filterOpen,
    setFilterOpen,
    activeFilterCount,
    hasPendingChanges,
    listParams,
    handleSearchChange,
    handleFilterChange,
    applyFilterValues,
  } = useLeaveApplicationFilters();

  const { fields: filterFields, hasOptionsError: filterOptionsFailed } =
    useLeaveApplicationFilterFields({
      isEnabled: filterOpen,
      isAttendanceClaim: isAttendanceClaimSelected(filterValues),
      summaryParams: listParams,
    });
  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    leaveApplicationListInfiniteQueryOptions(listParams),
  );

  const router = useRouter();
  const lastViewedApplicationId = useRevealLastViewedRow(VIEWED_ROW_LIST);
  const rememberViewedRow = useLastViewedRowStore((state) => state.rememberViewedRow);

  const markOpenedApplication = useCallback(
    (application: LeaveApplicationListItem) =>
      rememberViewedRow(VIEWED_ROW_LIST, { rowId: application.id, parentId: null }),
    [rememberViewedRow],
  );

  function openApplicationInNewTab(application: LeaveApplicationListItem) {
    markOpenedApplication(application);
    const { href } = router.buildLocation({
      to: '/hr-form/leave-applications/$applicationId',
      params: { applicationId: application.id },
    });
    window.open(href, NEW_TAB_TARGET, NEW_TAB_FEATURES);
  }

  const columns = useMemo(
    () =>
      getLeaveApplicationColumns({
        searchText: listParams.search,
        onOpenApplication: markOpenedApplication,
      }),
    [listParams.search, markOpenedApplication],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <LeaveApplicationListHeader
        searchText={searchText}
        onSearchChange={handleSearchChange}
        filterOpen={filterOpen}
        onFilterToggle={() => setFilterOpen((prev) => !prev)}
        activeFilterCount={activeFilterCount}
      />

      <FilterPanelCollapse isOpen={filterOpen}>
        <div className="flex flex-col gap-2">
          <AdvancedFilterPanel
            fields={filterFields}
            values={filterValues}
            onChange={handleFilterChange}
            onApply={applyFilterValues}
            hasPendingChanges={hasPendingChanges}
          />
          {filterOptionsFailed && (
            <p className="text-xs text-destructive">
              Some filter options could not be loaded. Refresh the page to try again.
            </p>
          )}
        </div>
      </FilterPanelCollapse>

      <LeaveApplicationSummaryCards params={listParams} />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load leave applications"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          stickyColumnId="userName"
          className="lg:flex-1"
          emptyIcon={CalendarOff}
          onRowClick={openApplicationInNewTab}
          isRowHighlighted={(application) => application.id === lastViewedApplicationId}
          {...loadMoreProps}
        />
      )}
    </div>
  );
}
